import React, { useEffect, useState } from "react";
import {
    Card, CardContent, Typography, CircularProgress, Button, List, ListItem, Grid, Box,
    Modal, FormControl, RadioGroup, FormControlLabel, Radio, FormLabel
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ComponentHeaderTitles from "../ComponentHeaderTitles";

import ProductDetailsButton from './ProductDetailsButton';
import ProductDetailsModal from './ProductDetailsModal';
import { formatNumberAsEuro } from "./helpers";
import { isRodaJCAction } from "../../config/generalConfig";



/**
 * Enum for the chosen product.
 * @readonly
 * @enum {string}
 */


const BACKEND_URL = process.env.BACKEND_URL + "/index_new";

/**
 * A component that allows the user to select a product from a list of options.
 *
 * @param {function} setChosenProduct - A function to update the chosen product.
 * @returns {JSX.Element} The ProductKiezer component.
 */
function PakketKiezer({ address, dubbeleMeter, chosenProduct, chosenUsage, returnedEnergyAmount, returnsEnergy, chosenEntity, setIsFetchingData, setChosenPakket, chosenPakket, agentID, returnsAvailableForAgent, onUpdateValidationErrors }) {
    const [products, setProducts] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [localIsFetchingData, setLocalIsFetchingData] = useState(null);

    // const [modalOpen, setModalOpen] = useState(false); // State to control modal visibility
    // const [modalProduct, setModalProduct] = useState(null); // State to hold the product for the modal
    const [openModals, setOpenModals] = useState({});

    /**
     * Opens the modal and sets the product for which the details should be shown.
     * @param {*} product The product to display details for.
     */
    const handleOpenModal = (productId) => {
        // setModalProduct(product); // Set the product for the modal
        // setModalOpen(true); // Open the modal
        setOpenModals({ ...openModals, [productId]: true });

    };

    // Define a styled Typography component for the total price
    const TotalPrice = styled(Typography)(({ theme }) => ({
        fontSize: '2em',
        fontWeight: 'bold'
    }));

    // Define a styled Box component for the price tags
    const PriceTag = styled(Box)(({ theme }) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 0),
        fontSize: '1.5em',
    }));
    const getPricingSummary = (product) => {
        let zakelijk = chosenEntity === "Zakelijk" ? true : false;
        const electricitySummary = zakelijk ? product.cost_specifications?.electricity?.total_with_price_cap : product.cost_specifications?.electricity?.total_with_price_cap_inc_vat;
        const gasSummary = zakelijk ? product.cost_specifications?.gas?.total_with_price_cap : product.cost_specifications?.gas?.total_with_price_cap_inc_vat;

        return {
            electricityTotal: electricitySummary,
            gasTotal: gasSummary
        };
    };

    const getTotalPrice = (product) => {
        let zakelijk = chosenEntity === "Zakelijk" ? true : false;
        const totalPrice = product.cost_specifications && (
            zakelijk
                ? product.cost_specifications.total.total
                : product.cost_specifications.total.total_inc_vat
        ) || 0;

        return totalPrice;
    }


    const getProductName = (product) => {
        return product.name;
    }


    /**
     * Closes the modal.
     */
    const handleCloseModal = (productId) => {
        // setModalOpen(false); // Close the modal
        const updatedOpenModals = { ...openModals };
        delete updatedOpenModals[productId];
        setOpenModals(updatedOpenModals);
    };
    /**
     * Sets the chosen pakket in the parent component and updates the selected product.
     * @param {*} product
     */
    const handleCardClick = (product) => {
        setChosenPakket(product);
        setSelectedProductId(product.id);

    };

    /**
     * Get the product parameter based on the chosen product.
     * @returns {string} The product parameter. False if the chosen product is invalid.
     */
    const get_product_parameter = () => {
        if (chosenProduct.includes("Gas") && !chosenProduct.includes("Electriciteit")) {
            return "g";
        } else if (chosenProduct.includes("Electriciteit") && !chosenProduct.includes("Gas")) {
            return "e";
        } else if (chosenProduct.includes("Gas") && chosenProduct.includes("Electriciteit")) {
            return "e_g";
        } else {
            console.error("Invalid chosen product", chosenProduct);
        }
        return false;
    }
    const get_return_parameters = () => {
        if (returnsEnergy) {
            return {
                "return": dubbeleMeter ? null : returnedEnergyAmount.return,
                "return_e_high": dubbeleMeter ? returnedEnergyAmount.return_high : null,
                "return_e_low": dubbeleMeter ? returnedEnergyAmount.return_low : null,
                "has_return": 1,
            }
        } else {
            return {
                "return": 0,
                "return_e_high": null,
                "return_e_low": null,
                "has_return": 0,
            }
        }
    }

    /**
     * Get the parameters for the API call.
     * @returns {Object} The parameters for the API call. False if the chosen product is invalid.
     * See https://developer.salesdock.nl/#b28c47a9-2d30-4310-957b-22e9fdb8bff0 for parameter documentation.
     */
    const get_parameters = () => {
        const product_parameter = get_product_parameter();
        if (!product_parameter) {
            return false;
        }

        const return_parameters = get_return_parameters();
        const housenumberSuffix = address.housenumberSuffix === "Geen" ? null : address.housenumberSuffix;
        const parameters_without_return = {
            "dynamic": 0,
            "roda": isRodaJCAction(),
            "m": "products",
            "postcode": address.postcode,
            "housenumber": address.housenumber,
            "suffix": housenumberSuffix,
            // if dubbeleMeter is true, tarifftype needs to be 1. Otherwise 0.
            "tarifftype": dubbeleMeter ? 1 : 0,
            "type": product_parameter,
            "usage_e_high": chosenUsage.e_high ? chosenUsage.e_high : null,
            "usage_e_low": chosenUsage.e_low ? chosenUsage.e_low : null,
            "usage_e_single": chosenUsage.e_single ? chosenUsage.e_single : null,
            "usage_g": chosenUsage.gas ? chosenUsage.gas : null,
            "business": chosenEntity === "Zakelijk" ? 1 : 0,
        }

        const parameters_with_return_parameters = {
            ...parameters_without_return,
            ...return_parameters
        }

        // add agentId to parameters if it exists
        if (agentID) {
            parameters_with_return_parameters.agent = agentID;
        }

        return parameters_with_return_parameters;
    }

    /**
     * Ensures that the first package is selected after the packages are fetched.
     * @param {*} data
     */
    function preselect_first_product(data) {
        setChosenPakket(data[0]);
    };

    /**
     * Ensures either first package is selected after the packages are fetched, or the previously selected package if the user comes back to this step
     * @param {*} data
     */
    function preselect_product(data) {
        if (data.length === 0) {
            return;
        }

        if (!chosenPakket) {
            // If no package was selected before, select the first one.
            setChosenPakket(data[0]);
        } else {
            // Check the package is still available
            const matchingProduct = data.find(p => p.id === chosenPakket.id);
            if (matchingProduct) {
                // If the previously selected package is still available, select it.
                setChosenPakket(matchingProduct);
            } else {
                // Otherwise select the first available package.
                setChosenPakket(data[0]);
            }
        }

    }

    function validateApiResponse(data) {
        if (!data || !(typeof data === 'object')) {
            console.error('Error fetching products: invalid response', data);
            return false;
        }
        // check if data[0] has name property
        if (!data[0].hasOwnProperty("name")) {
            console.error('Error fetching products: invalid response', data);
            return false;
        }
        return true;
    }

    async function get_data_from_api() {
        const url = new URL(BACKEND_URL);
        const params = get_parameters();
        if (!params) {
            return false;
        }
        url.search = new URLSearchParams(params).toString();

        setIsFetchingData(true);
        setLocalIsFetchingData(true);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
        try {
            if (response.ok) {
                var data = await response.json();
                // log type of data
                if (!validateApiResponse(data)) {
                    console.error('Error fetching products: invalid response', response);
                    onUpdateValidationErrors(['products', false]);
                    return false;
                }
                // filter out certain products
                //data = filterOutProductsToShowUser(data);
                setProducts(data);
                preselect_product(data);
                setIsFetchingData(false);
                setLocalIsFetchingData(false);
                onUpdateValidationErrors([]);
                return data;
            } else {
                setProducts([]);
                console.error('Error fetching products', response);
                setIsFetchingData(false);
                setLocalIsFetchingData(false);
                onUpdateValidationErrors(['products', false]);

                return false;
            }
        } catch (error) {
            console.error('Error fetching products', error);
            setIsFetchingData(false);
            setLocalIsFetchingData(false);
            onUpdateValidationErrors(['products', false]);
            return false;
        }
    }

    // When the component mounts, we call the API to get the products
    useEffect(() => {
        console.log(dubbeleMeter);
        get_data_from_api();
    }, [chosenUsage, chosenEntity]);

    useEffect(() => {
        // Alleen het eerste product selecteren als er nog geen product is geselecteerd
        // en als het chosenPakket nog niet is ingesteld (dus de gebruiker heeft nog geen keuze gemaakt)
        if (products.length > 0 && !selectedProductId && !chosenPakket) {
            const firstProduct = products[0];
            handleCardClick(firstProduct);
            setSelectedProductId(firstProduct.id); // Stel het geselecteerde product-ID in
        }
        // Als een pakket al is gekozen in een eerdere sessie/stap, herstel die keuze dan
        else if (chosenPakket) {
            setSelectedProductId(chosenPakket.id);
        }
    }, [products, chosenPakket, selectedProductId]);

    /**
     * TODO: Show a friendlier message when no products are found.
     */
    if (products.length === 0 && !localIsFetchingData) {
        onUpdateValidationErrors(['products', false]);
        return <Typography>Helaas zijn er geen pakketten gevonden.</Typography>;
    }

    /**
     * Sets the expandedId when the user clicks on the "Bekijk tarieven" button.
     * @param {*} productId
     */
    const handleExpandClick = (productId) => {
        setExpandedId(expandedId === productId ? null : productId);
    };

    /**
     * On the request of the client, certain products should not be shown to the user
     * @param {*} data
     */
    function filterOutProductsToShowUser(data) {
        const namesToFilterOut = [
            "ce-p3v-vd-vr-625-klantenservice", // particulier 3 jaar
            "ce-z3v-vd-vr-625-klantenservice" // mkb 3 jaar vast
        ];

        return data.filter(product => !namesToFilterOut.includes(product.identifier));
    }


    /**
 * Renders the modal with pricing details.
 * @returns JSX.Element
 */
    // const renderModal = () => {
    //     if (!modalProduct) return null; // Do not render the modal if there is no product
    //
    //     const { electricityTotal, gasTotal } = getPricingSummary(modalProduct);
    //     const electricityTotalParsed = parseEuropeanNumber(electricityTotal);
    //     const gasTotalParsed = parseEuropeanNumber(gasTotal);
    //
    //
    //     return (
    //         <Modal
    //             open={modalOpen}
    //             onClose={handleCloseModal}
    //             aria-labelledby="pricing-details-title"
    //             aria-describedby="pricing-details-description"
    //         >
    //             <div class="pricing-details-modal">
    //                 <Typography id="pricing-details-title" variant="h6" component="h2">
    //                     Prijsopbouw verwachte kosten
    //                 </Typography>
    //                 <div id="pricing-details-description">
    //                     {renderPricingDetails(modalProduct, electricityTotalParsed, gasTotalParsed)}
    //                 </div>
    //                 <Button onClick={handleCloseModal}>Sluiten</Button>
    //             </div>
    //         </Modal>
    //     );
    // };



    /**
     * We show a spinner when the products are being fetched.
     */
    // if (products.length === 0 && fetchingData) {
        // return (
        //     <div className="spinner-container">
        //         <CircularProgress className='data-fetch-spinner' />
        //     </div>
        // );
    // }
    const productEntries = Object.values(products); // Convert object to array so we can map over it
    const StyledPriceDetails = styled(Grid)(({ theme }) => ({
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 0),
        '&:last-child': {
            borderBottom: 'none',
        },
    }));

    const parseEuropeanNumber = (numberString) => {
        if (!numberString) {
            return null;
        }
        const noThousandsSeparator = numberString.replace(/\./g, '');
        const standardDecimalSeparator = noThousandsSeparator.replace(/,/, '.');
        return parseFloat(standardDecimalSeparator);
    };



    return (
        <div>
            <ComponentHeaderTitles title="Onze aanbieding" />

            <FormControl component="fieldset">
                <RadioGroup name="product" value={selectedProductId || products[0]?.id}>
                    {productEntries.map((product) => {
                        const { electricityTotal, gasTotal } = getPricingSummary(product);
                        const electricityTotalParsed = parseEuropeanNumber(electricityTotal);
                        const gasTotalParsed = parseEuropeanNumber(gasTotal);
                        const totalPrice = parseEuropeanNumber(getTotalPrice(product));


                        return (
                            <Card
                                key={product.id}
                                variant="outlined"
                                className={selectedProductId === product.id ? "selected" : ""}
                                onClick={() => handleCardClick(product)}
                            >
                                <CardContent>
                                    <div class="aanbod-header">
                                        {getProductName(product)}
                                    </div>

                                    <StyledPriceDetails container>
                                        <Grid item xs={6}>
                                            <TotalPrice>
                                                <div className="voorschot-per-maand-label">
                                                    Voorschot per maand
                                                </div>
                                            </TotalPrice>
                                        </Grid>
                                        <Grid item xs={6} style={{ textAlign: 'right' }}>
                                            <TotalPrice>
                                                <div className="voorschot-per-maand-prijs">
                                                    {/*{formatNumberAsEuro((parseFloat(electricityTotalParsed || 0) + parseFloat(gasTotalParsed || 0)) / 12) }*/}
                                                    {formatNumberAsEuro((parseFloat(totalPrice) / 12))}
                                                </div>
                                            </TotalPrice>
                                        </Grid>
                                    </StyledPriceDetails>

                                    {/*<StyledPriceDetails container>*/}
                                    {/*    <Grid item xs={6}>*/}
                                    {/*        <Typography variant="body2" color="textSecondary">*/}
                                    {/*            Verwachte kosten per jaar*/}
                                    {/*        </Typography>*/}
                                    {/*    </Grid>*/}
                                    {/*    <Grid item xs={6} style={{ textAlign: 'right' }}>*/}
                                    {/*        <Typography variant="body2" color="textSecondary">*/}
                                    {/*            { formatNumberAsEuro(*/}

                                    {/*                parseFloat(electricityTotalParsed || 0) + parseFloat(gasTotalParsed || 0)*/}

                                    {/*            ) }*/}

                                    {/*        </Typography>*/}
                                    {/*    </Grid>*/}
                                    {/*</StyledPriceDetails>*/}
                                    <Grid container justifyContent="flex-end">
                                        <Grid item>
                                            <ProductDetailsButton onClick={() => handleOpenModal(product.id)} />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <ProductDetailsModal
                                    modalOpen={openModals[product.id] || false}
                                    handleCloseModal={() => handleCloseModal(product.id)}
                                    dubbeleMeter={dubbeleMeter}
                                    modalProduct={product}
                                    chosenEntity={chosenEntity}
                                    electricityTotalParsed={electricityTotalParsed}
                                    gasTotalParsed={gasTotalParsed}
                                    returnsAvailableForAgent={returnsAvailableForAgent}
                                />
                            </Card>

                        );
                    })}
                </RadioGroup>

            </FormControl>
            {/* Render the modal outside of the map function */}

        </div>
    );
}
PakketKiezer.displayName = "PakketKiezer";
export default PakketKiezer;
